/**
 * ThreadRenderer — sprite-based photoreal embroidery preview.
 *
 * Inspired by Wilcom TrueView / Hatch 3 stitch preview. Pre-rendered
 * thread sprites are blitted at each stitch position, giving a true
 * 3D thread look at 5-10× the speed of per-stitch gradient creation.
 *
 * Render order per shape:
 *   1. Drop-shadow pass: thread sprite drawn dark + offset, gives depth
 *   2. Color pass: thread sprite at each stitch position
 *
 * Sprites are cached per (color, size) tuple so re-renders are cheap.
 */

// ── Sprite dimensions (offscreen canvas size) ─────────────────────
//   Bigger sprite = sharper at high zoom but slower to create. 64×16
//   is the sweet spot — looks crisp up to ~30 px on screen.
const SPRITE_W = 64
const SPRITE_H = 16

// Cache: key = `${color}-${variant}` → HTMLCanvasElement
const spriteCache = new Map()

// Cache: key = `${fabricColor}-${seed}` → HTMLCanvasElement (tile)
const fabricCache = new Map()


// ── Color helpers ──────────────────────────────────────────────────

function parseHex (hex) {
  const s = (hex || '#000000').replace('#', '')
  if (s.length === 6) {
    return [
      parseInt(s.slice(0, 2), 16),
      parseInt(s.slice(2, 4), 16),
      parseInt(s.slice(4, 6), 16),
    ]
  }
  return [0, 0, 0]
}

function rgb (r, g, b, a = 1) {
  return `rgba(${r | 0},${g | 0},${b | 0},${a})`
}

function lighten ([r, g, b], amount) {
  return [
    Math.min(255, r + (255 - r) * amount),
    Math.min(255, g + (255 - g) * amount),
    Math.min(255, b + (255 - b) * amount),
  ]
}

function darken ([r, g, b], amount) {
  return [r * (1 - amount), g * (1 - amount), b * (1 - amount)]
}


// ── Thread sprite generation ───────────────────────────────────────

/**
 * Build a thread sprite — a horizontal "thread element" with
 * cross-section gradient + sheen + soft end-caps. Returns a canvas.
 *
 * @param {string} colorHex  base thread color, e.g. '#3f47cc'
 * @param {'color' | 'shadow'} variant
 *   'color' = the thread itself with sheen
 *   'shadow' = darker, slightly soft (for the drop-shadow pass)
 */
function buildThreadSprite (colorHex, variant = 'color') {
  const key = `${colorHex}-${variant}`
  const cached = spriteCache.get(key)
  if (cached) return cached

  const cv = document.createElement('canvas')
  cv.width = SPRITE_W
  cv.height = SPRITE_H
  const c = cv.getContext('2d')
  const rgb0 = parseHex(colorHex)

  if (variant === 'shadow') {
    // Soft dark blob underneath the thread for depth. Just a vertical
    // gradient with low alpha.
    const g = c.createLinearGradient(0, 0, 0, SPRITE_H)
    g.addColorStop(0, 'rgba(0,0,0,0)')
    g.addColorStop(0.5, 'rgba(0,0,0,0.18)')
    g.addColorStop(1, 'rgba(0,0,0,0)')
    c.fillStyle = g
    // Soft elliptical-ish mask via end-cap fade
    const fadePx = SPRITE_H * 0.6
    c.fillRect(fadePx, 0, SPRITE_W - 2 * fadePx, SPRITE_H)
    // Faded ends
    const lEnd = c.createLinearGradient(0, 0, fadePx, 0)
    lEnd.addColorStop(0, 'rgba(0,0,0,0)')
    lEnd.addColorStop(1, 'rgba(0,0,0,0.10)')
    c.fillStyle = lEnd
    c.fillRect(0, 0, fadePx, SPRITE_H)
    const rEnd = c.createLinearGradient(SPRITE_W - fadePx, 0, SPRITE_W, 0)
    rEnd.addColorStop(0, 'rgba(0,0,0,0.10)')
    rEnd.addColorStop(1, 'rgba(0,0,0,0)')
    c.fillStyle = rEnd
    c.fillRect(SPRITE_W - fadePx, 0, fadePx, SPRITE_H)
    spriteCache.set(key, cv)
    return cv
  }

  // Color variant: completely flat base color, no cross-section
  // gradient, no horizontal end-cap fade. Any per-thread visual
  // variation (sheen, dark edges, alpha fade) creates periodic
  // artifacts that align across adjacent rows in dense tatami fills
  // and read as visible stripes / "missing chunks." Pure flat = solid
  // coverage on fills, at the cost of 3D thread depth.
  c.fillStyle = rgb(...rgb0)
  c.fillRect(0, 0, SPRITE_W, SPRITE_H)

  spriteCache.set(key, cv)
  return cv
}


// ── Fabric texture generation ──────────────────────────────────────

/**
 * Build a tileable fabric texture. Subtle noise + faint weave pattern.
 * Single tile is ~256×256 px and tiled across the design via
 * createPattern.
 */
function buildFabricTile (fabricColorHex, seed = 1) {
  const key = `${fabricColorHex}-${seed}`
  const cached = fabricCache.get(key)
  if (cached) return cached

  const SIZE = 256
  const cv = document.createElement('canvas')
  cv.width = SIZE
  cv.height = SIZE
  const c = cv.getContext('2d')
  const [r, g, b] = parseHex(fabricColorHex)

  // Base color
  c.fillStyle = rgb(r, g, b)
  c.fillRect(0, 0, SIZE, SIZE)

  // Noise speckles — tiny dark/light dots for woven cotton feel
  const img = c.getImageData(0, 0, SIZE, SIZE)
  const data = img.data
  for (let i = 0; i < data.length; i += 4) {
    const n = (Math.random() - 0.5) * 18
    data[i]     = Math.max(0, Math.min(255, data[i]     + n))
    data[i + 1] = Math.max(0, Math.min(255, data[i + 1] + n))
    data[i + 2] = Math.max(0, Math.min(255, data[i + 2] + n))
  }
  c.putImageData(img, 0, 0)

  // Subtle weave: thin horizontal + vertical lines at very low alpha
  c.fillStyle = 'rgba(0,0,0,0.06)'
  for (let y = 0; y < SIZE; y += 4) c.fillRect(0, y, SIZE, 1)
  c.fillStyle = 'rgba(255,255,255,0.04)'
  for (let x = 0; x < SIZE; x += 4) c.fillRect(x, 0, 1, SIZE)

  fabricCache.set(key, cv)
  return cv
}


// ── Public API ─────────────────────────────────────────────────────

/**
 * Draw the fabric backdrop. Call this BEFORE drawing any shapes.
 *
 * @param ctx        Canvas2D context (already translated to design space)
 * @param bounds     {minX, minY, maxX, maxY} of the design in mm
 * @param fabricColor hex string for fabric (e.g. '#f5f0e8')
 */
export function drawFabric (ctx, bounds, fabricColor) {
  if (!bounds) return
  const tile = buildFabricTile(fabricColor)
  const pattern = ctx.createPattern(tile, 'repeat')
  if (!pattern) return
  // Pattern is in design (mm) space; scale tile to ~6mm so it looks fine.
  // matrix maps tile pixels → mm.
  const mat = new DOMMatrix().scale(6 / tile.width)
  if (pattern.setTransform) pattern.setTransform(mat)
  ctx.fillStyle = pattern
  const pad = 4
  ctx.fillRect(bounds.minX - pad, bounds.minY - pad,
               (bounds.maxX - bounds.minX) + 2 * pad,
               (bounds.maxY - bounds.minY) + 2 * pad)
}


/**
 * Render one shape's stitches as 3D thread sprites.
 *
 * Two passes:
 *   1. Shadow sprite (offset, low-alpha dark) underneath
 *   2. Color thread sprite at each stitch position
 *
 * @param ctx        Canvas2D context (in design-mm space)
 * @param stitchSegs Array of [x1, y1, x2, y2] in mm
 * @param colorHex   thread color
 * @param threadW    thread width in mm (default 0.20)
 * @param zoom       current canvas zoom (used to clamp min thread size)
 */
export function drawThreads (ctx, stitchSegs, colorHex, threadW = 0.20, zoom = 1) {
  if (!stitchSegs || stitchSegs.length === 0) return

  const w = Math.max(threadW, 0.6 / zoom)
  const colorSprite = buildThreadSprite(colorHex, 'color')
  const sw = SPRITE_W
  const sh = SPRITE_H

  // Single-pass render. The shadow pass was creating periodic dark
  // accumulation between adjacent tatami rows that read as visible
  // stripes/scribbles. The softened cross-section gradient already
  // provides enough 3D feel without the offset shadow.
  for (let i = 0; i < stitchSegs.length; i++) {
    const seg = stitchSegs[i]
    const x1 = seg[0], y1 = seg[1], x2 = seg[2], y2 = seg[3]
    const dx = x2 - x1, dy = y2 - y1
    const len = Math.hypot(dx, dy)
    if (len < 0.05) continue
    const angle = Math.atan2(dy, dx)
    ctx.save()
    ctx.translate((x1 + x2) / 2, (y1 + y2) / 2)
    ctx.rotate(angle)
    ctx.scale(len / sw, w / sh)
    ctx.drawImage(colorSprite, -sw / 2, -sh / 2)
    ctx.restore()
  }
}


/** Clear sprite caches — call on color theme change to reclaim memory. */
export function clearCaches () {
  spriteCache.clear()
  fabricCache.clear()
}
